import React, { useCallback, useContext } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useTranslation } from 'react-i18next'
import Axios from '../../api'
import { ErrorModalContext } from '../../context/errorModalContext'

interface MoodleLinkResponse {
  url: string
  expires_in: number
}

// Adresse de Moodle où une session personnifiée a été ouverte, pour la fermer au « Libérer ».
const MOODLE_ORIGIN_KEY = 'moodle_impersonation_origin'

/**
 * Demande à la passerelle l'adresse à usage unique qui ouvre la session Moodle du membre
 * personnifié (trainingId 0 : tableau de bord) et retient l'adresse de Moodle pour « Libérer ».
 * Barre oblique initiale requise : l'intercepteur (src/api/index.ts) ne garde le jeton Auth0 de
 * l'employé que si l'URL contient « /personnification/ ».
 */
export const requestMoodleLink = async (
  staffToken: string,
  delegationToken: string,
  trainingId: string | number,
): Promise<string> => {
  const { data } = await Axios.post<MoodleLinkResponse>(
    '/personnification/moodle-link',
    { delegation_token: delegationToken, training_id: Number(trainingId) || 0 },
    { headers: { Authorization: `Bearer ${staffToken}` } },
  )
  localStorage.setItem(MOODLE_ORIGIN_KEY, new URL(data.url).origin)
  return data.url
}

/**
 * Ferme la session Moodle personnifiée, s'il y en a une (bouton « Libérer »). À appeler avant le
 * vidage du localStorage. Requête « no-cors » avec les cookies : Moodle (même site que l'Espace
 * client) ne ferme sa session que si elle est personnifiée. N'échoue jamais.
 */
export const endMoodleImpersonation = async (): Promise<void> => {
  const origin = localStorage.getItem(MOODLE_ORIGIN_KEY)
  if (!origin) {
    return
  }
  try {
    await fetch(`${origin}/local/acq/impersonate_end.php`, {
      method: 'POST',
      mode: 'no-cors',
      credentials: 'include',
      keepalive: true,
    })
  } catch (error) {
    console.warn('[IMPERSONATION] Moodle session could not be ended:', error)
  }
  localStorage.removeItem(MOODLE_ORIGIN_KEY)
}

/**
 * Ouvre une formation Moodle depuis « Mes formations » (ticket 5277).
 *
 * Hors personnification, le lien Moodle s'ouvre normalement (le gestionnaire ne fait rien).
 * Pendant une personnification, le SSO Moodle connecterait l'employé : on demande donc à la
 * passerelle, au moment du clic, une adresse à usage unique qui ouvre la session Moodle du
 * membre personnifié. La passerelle exige le jeton Auth0 de l'employé (rôle « Impersonate »)
 * et le jeton de délégation ; l'intercepteur d'Axios ne remplace pas l'Authorization sur les
 * routes /personnification/.
 */
const useOpenMoodleLink = () => {
  const { getAccessTokenSilently } = useAuth0()
  const { setShowModal, setErrorMessage } = useContext(ErrorModalContext)
  const { t } = useTranslation()

  return useCallback(
    (event: React.MouseEvent, trainingId: string | number) => {
      const delegationToken = localStorage.getItem('delegation_auth_token')
      const isImpersonating = localStorage.getItem('is_impersonating') === 'true'
      if (!isImpersonating || !delegationToken) {
        return
      }

      event.preventDefault()
      // Onglet ouvert pendant le clic : ouvert après l'appel réseau, il serait bloqué par le
      // navigateur comme fenêtre surgissante.
      const moodleWindow = window.open('', '_blank')
      if (moodleWindow) {
        moodleWindow.opener = null
      }

      const openLink = async () => {
        try {
          const token = await getAccessTokenSilently()
          const url = await requestMoodleLink(token, delegationToken, trainingId)
          if (moodleWindow) {
            moodleWindow.location.href = url
          } else {
            window.location.href = url
          }
        } catch (error) {
          moodleWindow?.close()
          setErrorMessage(t('pages.personification.moodleError') ?? '')
          setShowModal(true)
        }
      }
      openLink()
    },
    [getAccessTokenSilently, setErrorMessage, setShowModal, t],
  )
}

export default useOpenMoodleLink
